import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";

import { Input } from "../../../common/Input";
import { DepartmentPickerField } from "../../../common/selectComponents/DepartmentPicker"; // ADDED
import { PositionPickerField } from "../../../common/selectComponents/PositionPicker"; // ADDED
import { ConfirmationModal } from "../../../common/Modal";

/**
 * AddEditCrewDefinitionModal
 *
 * Props:
 *   isOpen       — boolean
 *   onClose      — () => void
 *   mode         — "add" | "edit"
 *   selectedData — row object when editing, null when adding
 *   onAdd        — (rowForm) => void
 *   onUpdate     — (rowForm, index) => void
 *   options      — no longer needed for pickers (they fetch their own data)
 *
 * MODIFIED: Replaced Select dropdowns with DepartmentPickerField and PositionPickerField
 */
export const AddEditCrewDefinitionModal = ({
	isOpen,
	onClose,
	mode,
	selectedData,
	onAdd,
	onUpdate,
	options, // KEPT for backward compatibility, but not used by pickers
}) => {
	// MODIFIED: updated emptyForm to include code/description fields for pickers
	const emptyForm = {
		departmentId: "",
		departmentCode: "", // ADDED
		departmentDescription: "", // ADDED
		positionId: "",
		positionCode: "", // ADDED
		positionDescription: "", // ADDED
		allowedCrews: "",
	};

	const [form, setForm] = useState(emptyForm);
	const [errors, setErrors] = useState({});
	const [showConfirmation, setShowConfirmation] = useState(false);

	// ── Sync form when modal opens ──────────────────────────────────────────
	useEffect(() => {
		if (isOpen) {
			setErrors({});
			setForm(
				mode === "edit" && selectedData
					? {
						departmentId: selectedData.departmentId || "",
						departmentCode: selectedData.departmentCode || "", // ADDED
						departmentDescription: selectedData.departmentDescription || "", // ADDED
						positionId: selectedData.positionId || "",
						positionCode: selectedData.positionCode || "", // ADDED
						positionDescription: selectedData.positionDescription || "", // ADDED
						allowedCrews: selectedData.allowedCrews ?? "",
					}
					: emptyForm
			);
		}
	}, [isOpen, mode, selectedData]);

	const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

	// ADDED: handler for Department selection
	const handleDepartmentSelect = (department) => {
		set("departmentId", department.id);
		set("departmentCode", department.code);
		set("departmentDescription", department.description);
	};

	// ADDED: handler for Department clear
	const handleDepartmentClear = () => {
		set("departmentId", "");
		set("departmentCode", "");
		set("departmentDescription", "");
	};

	// ADDED: handler for Position selection
	const handlePositionSelect = (position) => {
		set("positionId", position.id);
		set("positionCode", position.code);
		set("positionDescription", position.description);
	};

	// ADDED: handler for Position clear
	const handlePositionClear = () => {
		set("positionId", "");
		set("positionCode", "");
		set("positionDescription", "");
	};

	// ── Validate ────────────────────────────────────────────────────────────
	const validate = () => {
		const e = {};
		if (!form.departmentId) e.departmentId = "Department is required.";
		if (!form.positionId) e.positionId = "Position is required.";
		if (form.allowedCrews === "" || form.allowedCrews === null || form.allowedCrews === undefined)
			e.allowedCrews = "Allowed Crews is required.";
		else if (isNaN(Number(form.allowedCrews)) || Number(form.allowedCrews) < 0)
			e.allowedCrews = "Must be a valid non-negative number.";
		return e;
	};

	// ── Submit ──────────────────────────────────────────────────────────────
	const handleSubmit = () => {
		const e = validate();
		if (Object.keys(e).length) { setErrors(e); return; }
		setShowConfirmation(true);
	};

	const doConfirm = () => {
		if (mode === "add") {
			onAdd(form);
		} else {
			onUpdate(form, selectedData._index);
		}
		setShowConfirmation(false);
		onClose();
	};

	if (!isOpen) return null;

	return (
		<>
			<div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 !mt-0">
				<div className="bg-white w-full max-w-lg rounded-lg shadow-2xl flex flex-col">

					{/* Header */}
					<div className="flex justify-between items-center px-5 py-3 border-b bg-gray-50 rounded-t-lg">
						<h3 className="text-sm font-bold text-blue-900">
							{mode === "add" ? "Add Crew Definition" : "Edit Crew Definition"}
						</h3>
						<button
							onClick={onClose}
							className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer"
						>
							<CloseIcon style={{ fontSize: 18 }} />
						</button>
					</div>

					{/* Body — MODIFIED: replaced Select with pickers */}
					<div className="p-5">
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							{/* REPLACED: Department Select with DepartmentPickerField */}
							<DepartmentPickerField
								label="Department"
								departmentId={form.departmentId || ""}
								displayValue={form.departmentDescription || ""}
								onSelect={handleDepartmentSelect}
								onClear={handleDepartmentClear}
								isRequired={true}
								error={!!errors.departmentId}
								errorMessage={errors.departmentId}
								allowClear={false}
							/>
							
							{/* REPLACED: Position Select with PositionPickerField */}
							<PositionPickerField
								label="Position"
								positionId={form.positionId || ""}
								displayValue={form.positionDescription || ""}
								onSelect={handlePositionSelect}
								onClear={handlePositionClear}
								isRequired={true}
								error={!!errors.positionId}
								errorMessage={errors.positionId}
								allowClear={false}
							/>
							
							{/* KEPT: Allowed Crews */}
							<Input
								label="Allowed Crews:"
								value={form.allowedCrews}
								onChange={(e) => set("allowedCrews", e.target.value)}
								placeholder="0"
								type="number"
								isRequired={true}
								error={!!errors.allowedCrews}
								errorMessage={errors.allowedCrews}
							/>
						</div>
					</div>

					{/* Footer */}
					<div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50 rounded-b-lg">
						<button
							onClick={onClose}
							className="px-4 py-2 bg-gray-400 text-white text-sm rounded cursor-pointer hover:bg-gray-500 border-none transition-colors"
						>
							Cancel
						</button>
						<button
							onClick={handleSubmit}
							className="px-6 py-2 bg-blue-600 text-white text-sm font-bold rounded cursor-pointer hover:bg-blue-700 border-none transition-colors"
						>
							{mode === "add" ? "Add" : "Update"}
						</button>
					</div>
				</div>
			</div>

			<ConfirmationModal
				visible={showConfirmation}
				onClose={() => setShowConfirmation(false)}
				onConfirm={doConfirm}
				title={mode === "add" ? "Add Crew Definition" : "Update Crew Definition"}
				message={
					mode === "add"
						? "Add this crew definition?"
						: "Update this crew definition?"
				}
			/>
		</>
	);
};